import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const empty = { name: "", phone: "", email: "", address: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error("Ingresá el nombre"); return; }
    try {
      if (editId) await api.put(`/clients/${editId}`, editing);
      else await api.post("/clients", editing);
      toast.success("Cliente guardado");
      setEditing(null); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar este cliente definitivamente?")) return;
    try { await api.delete(`/clients/${id}`); load(); toast.success("Cliente eliminado"); } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search));

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">Clientes</h1>
        <div className="flex gap-3">
          <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-sm w-48 h-9" data-testid="client-search-input" />
          <Button onClick={() => { setEditing({ ...empty }); setEditId(null); }} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo cliente
          </Button>
        </div>
      </div>

      {filtered.length === 0 && <p className="text-neutral-500 text-sm">No hay clientes. Se agregan desde consultas o manualmente.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((c) => (
          <div key={c.id} className="bg-[#171717] border border-neutral-800 rounded-md p-5 hover:border-neutral-700 transition-colors flex flex-col" data-testid={`client-card-${c.id}`}>
            <p className="text-sm font-medium truncate">{c.name}</p>
            <p className="text-xs text-neutral-500 mt-1">{c.phone}</p>
            {(c.email || c.address) && (
              <p className="text-xs text-neutral-500 mt-0.5 truncate">{[c.email, c.address].filter(Boolean).join(" · ")}</p>
            )}
            <div className="flex gap-2 mt-4 pt-3 border-t border-neutral-800">
              <Button size="sm" variant="outline" onClick={() => navigate("/panel/whatsapp", { state: { phone: c.phone, nombre: c.name, clientId: c.id } })}
                className="flex-1 border-[#25D366]/50 text-[#25D366] rounded-sm h-8 px-2 hover:bg-[#25D366]/10 hover:text-[#25D366]" data-testid={`client-whatsapp-${c.id}`}>
                <MessageCircle className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing({ name: c.name, phone: c.phone, email: c.email || "", address: c.address || "", notes: c.notes || "" }); setEditId(c.id); }}
                className="flex-1 border-neutral-700 rounded-sm h-8 px-2 hover:bg-neutral-800 hover:text-white" data-testid={`client-edit-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="flex-1 border-neutral-700 rounded-sm h-8 px-2 text-red-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`client-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editId ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder="Nombre *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="client-name-input" />
              <Input placeholder="Teléfono / WhatsApp" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="client-phone-input" />
              <Input placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="client-email-input" />
              <Input placeholder="Dirección" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="client-address-input" />
              <Textarea placeholder="Notas" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="client-notes-input" />
              <Button onClick={save} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="client-save-btn">Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
